// app/javascript/controllers/rsvp_lookup_controller.js
import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

export default class extends Controller {
    static targets = ["form", "title", "body", "changeBtn", "contributeBtn", "email"]
    static values  = {
        lookupUrl: String,     // e.g. /rsvp/:token/lookup
        token: String          // party token (optional)
    }

    connect() {
        this._debounceTimer = null
        this._lastEmail = null
        this._shownForEmail = new Set() // avoid re-showing repeatedly this session
    }

    // Hook these to email input
    onEmailInput() {
        this._debouncedCheck()
    }
    onEmailBlur() {
        this._debouncedCheck({ immediate: true })
    }

    // Optional safety: call on submit if you want a last-minute prompt
    async onSubmitCheck(event) {
        // Only run if there is a valid email and we haven't shown for it
        const email = this._currentEmail()
        if (!this._isValidEmail(email) || this._shownForEmail.has(email)) return

        const data = await this._fetch(email).catch(() => null)
        if (data?.has_any) {
            event.preventDefault()
            this._showModal(email, data)
        }
    }

    // ---- internals ----

    _debouncedCheck({ immediate = false } = {}) {
        const email = this._currentEmail()
        if (!this._isValidEmail(email)) return

        // Skip if same and we already asked
        if (this._shownForEmail.has(email)) return
        if (email === this._lastEmail && !immediate) return

        clearTimeout(this._debounceTimer)
        const run = async () => {
            this._lastEmail = email
            const data = await this._fetch(email).catch(() => null)
            if (data?.has_any) this._showModal(email, data)
        }
        this._debounceTimer = setTimeout(run, immediate ? 0 : 400)
    }

    async _fetch(email) {
        const url = new URL(this.lookupUrlValue, window.location.origin)
        url.searchParams.set("email", email)
        const resp = await fetch(url.toString(), { headers: { "Accept": "application/json" } })
        if (!resp.ok) throw new Error("lookup failed")
        return await resp.json()
    }

    _currentEmail() {
        // prefer explicit email target, else find in form
        const el = this.hasEmailTarget ? this.emailTarget : this.formTarget?.querySelector('[name="email"]')
        return (el?.value || "").trim().toLowerCase()
    }

    _isValidEmail(e) {
        if (!e) return false
        // light validation, same as your FE validator
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
    }

    _moneyCents(cents) {
        const v = Math.max(0, parseInt(cents || 0, 10)) / 100
        return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }

    _showModal(email, data) {
        const modalEl = document.getElementById("rsvpLookupModal")
        if (!modalEl) return
        // Build copy
        const hasRSVP = !!data.invite_status
        const hasContrib = (data.contributed_cents || 0) > 0

        if (hasRSVP) {
            const status = (data.invite_status || "").toUpperCase()
            this.titleTarget.textContent = "Looks familiar"
            this.bodyTarget.innerHTML = ` You have already RSVP’d <strong>${status === "ATTENDING" ? "Attending" : "Cannot Attend"}</strong>` +
                (hasContrib ? ` and given a contribution. <BR> You can change your RSVP easily and/or contribute more.` : " and have NOT given a contribution yet..")
        } else if (hasContrib) {
            this.titleTarget.textContent = "We found a previous contribution"
            this.bodyTarget.innerHTML = `This email has already contributed <strong>${this._moneyCents(data.contributed_cents)}</strong> to this event. You can contribute again or continue.`
        } else {
            // Shouldn’t happen (has_any false), but guard anyway
            return
        }

        // Buttons
        // const changeHref = this._linkForChangeRSVP()
        // if (changeHref) this.changeBtnTarget.setAttribute("href", changeHref)
        //
        // const contribHref = this._linkForContribute(hasContrib)
        // if (contribHref) this.contributeBtnTarget.setAttribute("href", contribHref)
        // this.contributeBtnTarget.textContent = hasContrib ? "Contribute again" : "Contribute"

        // Mark as shown for this email for this session
        this._shownForEmail.add(email)

        // Show modal (Bootstrap
        const ev = bootstrap.Modal.getOrCreateInstance(modalEl)
        ev.show()
    }

    _linkForChangeRSVP() {
        // Your standard attend path
        // (If you support an explicit “change” screen, swap the path here.)
        const token = this.tokenValue || this._deriveTokenFromUrl()
        return token ? `/rsvp/${encodeURIComponent(token)}/attend` : null
    }

    _linkForContribute(alreadyContributed) {
        const token = this.tokenValue || this._deriveTokenFromUrl()
        if (!token) return null
        const url = new URL(`/rsvp/${encodeURIComponent(token)}/contribute`, window.location.origin)
        url.searchParams.set("intent", "attend")
        return url.pathname + url.search
    }

    _deriveTokenFromUrl() {
        // Optional helper if you don't pass tokenValue
        const m = window.location.pathname.match(/\/rsvp\/([^\/\?]+)/)
        return m ? m[1] : null
    }
}